/**
 * Anyone Home Chatbot Block (OmniChannel / "omnibot")
 *
 * Injection block that loads the Anyone Home OmniChannel communication widget.
 * The vendor snippet pasted on the property looks like:
 *
 *   <link rel='stylesheet' href='https://www.myshowing.com/css/omnichannel/omnichannel_communication.css'>
 *   <script src='https://www.myshowing.com/js/omnichannel/omnichannel_communication.js'></script>
 *   <script type='text/javascript'>window.OmniChannel.init('aGXht7qWqcp9t8JeCf')</script>
 *
 * The only per-property value is the account id handed to OmniChannel.init().
 * The CSS/JS URLs are constant, so rather than re-injecting whatever blob is
 * pasted we extract the id and build canonical nodes (stylesheet link + vendor
 * script + init call). The field accepts either the full snippet OR the bare id
 * so the ops team can paste whichever they have on hand.
 *
 * Why singleton + never-tear-down: OmniChannel paints its launcher directly
 * into the DOM (outside our tracking) and re-running init() duplicates the
 * widget. So we inject once and leave it alone for the tab's lifetime, which
 * survives StrictMode and SPA navigation remounts. Subsequent edits to the id
 * won't reflect until a hard refresh — an acceptable trade for not duplicating
 * the widget. This mirrors the entrata-chatbot / rentcafe-chatbot blocks.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const INJECTED_NODE_ATTRIBUTE = 'data-anyonehome-chatbot-node';

const STYLESHEET_HREF =
  'https://www.myshowing.com/css/omnichannel/omnichannel_communication.css';
const SCRIPT_SRC = 'https://www.myshowing.com/js/omnichannel/omnichannel_communication.js';

// Pull the account id out of `OmniChannel.init('...')` / `OmniChannel.init("...")`.
const OMNICHANNEL_ID_PATTERN = /OmniChannel\.init\s*\(\s*(?:"([^"]+)"|'([^']+)')\s*\)/i;
// Markers that mean we were handed a snippet (so a missing id is a real failure,
// not a bare id to use verbatim).
const SCRIPT_SNIPPET_PATTERN = /<script[\s>]|<link[\s>]|OmniChannel/i;

type AnyoneHomeChatbotState = {
  nodes: Node[];
  value: string | null;
};

function normalizeValue(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * Accept the full embed snippet OR a bare account id. If the value contains an
 * `OmniChannel.init('...')` call, use the captured id. Otherwise, if it looks
 * like a snippet but has no id, treat it as unusable (null). A plain string is
 * assumed to be the id itself.
 */
function extractAccountId(value: string | null | undefined) {
  const normalized = normalizeValue(value);
  if (!normalized) return null;

  const match = normalized.match(OMNICHANNEL_ID_PATTERN);
  const accountId = normalizeValue(match?.[1] ?? match?.[2]);
  if (accountId) return accountId;

  if (SCRIPT_SNIPPET_PATTERN.test(normalized)) return null;
  return normalized;
}

/**
 * Append a node to <head>, resolving only once it has "run". External
 * `<script src>` elements resolve on load/error; everything else (inline
 * scripts, which execute synchronously on append, and non-script nodes)
 * resolves immediately. Awaiting this between nodes serializes execution so the
 * vendor script defines `window.OmniChannel` before the inline init() runs —
 * dynamically-inserted scripts are async by default, which would otherwise
 * throw "OmniChannel is not defined".
 */
function appendHeadNode(node: Node) {
  if (node instanceof HTMLScriptElement && node.src) {
    return new Promise<void>(resolve => {
      node.addEventListener('load', () => resolve(), { once: true });
      node.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(node);
    });
  }

  document.head.appendChild(node);
  return Promise.resolve();
}

function createHeadNodes(accountId: string) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = STYLESHEET_HREF;
  link.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');

  const vendorScript = document.createElement('script');
  vendorScript.src = SCRIPT_SRC;
  vendorScript.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');

  const initScript = document.createElement('script');
  initScript.type = 'text/javascript';
  initScript.textContent = `window.OmniChannel.init(${JSON.stringify(accountId)})`;
  initScript.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');

  return [link, vendorScript, initScript];
}

function getAnyoneHomeChatbotState() {
  const windowWithState = window as Window & {
    __anyoneHomeChatbotState?: AnyoneHomeChatbotState;
  };

  // Single tab-wide instance. Once we've injected the vendor script, we
  // remember it here so subsequent mounts (StrictMode, SPA navigation, etc.)
  // skip re-injection — OmniChannel paints its widget directly in <body>, and
  // re-running init() duplicates the widget.
  if (windowWithState.__anyoneHomeChatbotState) {
    return windowWithState.__anyoneHomeChatbotState;
  }

  const state: AnyoneHomeChatbotState = {
    nodes: [],
    value: null,
  };

  windowWithState.__anyoneHomeChatbotState = state;
  return state;
}

function AnyoneHomeChatbotComponent(_props: BlockProps) {
  const accountId = extractAccountId(
    useBlockToken('account_id', {
      defaultField: 'custom_anyonehome_chatbot',
    }),
  );

  useEffect(() => {
    if (!accountId) return;

    const state = getAnyoneHomeChatbotState();

    // Load the vendor script exactly once per tab and never tear it down.
    // OmniChannel creates its launcher DOM directly in <body> — outside our
    // tracking. Tearing down + re-injecting on remount or SPA navigation makes
    // the vendor paint a SECOND widget on top of the existing one, since the
    // original body DOM isn't ours to clean up. The safe-and-simple answer is
    // to behave like a script in index.html: inject once, leave alone for the
    // tab's lifetime. Subsequent edits to the id won't reflect until a hard
    // refresh — an acceptable trade for not duplicating the widget.
    if (state.value) return;

    const nodes = createHeadNodes(accountId);

    // Mark injected synchronously so a concurrent remount can't kick off a
    // second sequential load before this one finishes.
    state.value = accountId;
    state.nodes = nodes;

    // Load sequentially: the external vendor script must finish executing
    // before the inline init() node is appended, so window.OmniChannel exists
    // when init() runs. See appendHeadNode.
    void (async () => {
      for (const node of nodes) {
        await appendHeadNode(node);
      }
    })();
  }, [accountId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'AnyoneHomeChatbot',
  displayName: 'Anyone Home Chatbot',
  tokens: {
    account_id: {
      label: 'Anyone Home Embed Script or ID',
      type: 'text',
      defaultField: 'custom_anyonehome_chatbot',
      suggestedFields: ['custom_anyonehome_chatbot'],
      description:
        "Anyone Home OmniChannel embed snippet or account ID. Full snippets are parsed for the OmniChannel.init('...') value.",
      required: true,
    },
  },
};

export const anyonehomeChatbotBlock: BlockDefinition = {
  id: 'anyonehome-chatbot',
  name: 'Anyone Home Chatbot',
  description:
    'Anyone Home OmniChannel communication widget ("omnibot") injected from the property embed snippet or account ID. The floating launcher is the UI — clicking it opens the chat / contact flow.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: AnyoneHomeChatbotComponent,
  contentSchema,
  tags: ['anyonehome', 'anyone home', 'omnichannel', 'omnibot', 'chatbot', 'chat', 'contact', 'widget'],
};
